/**
 * The chase: the grey space behind the tool row.
 *
 * A bay-and-chase fab is two rooms, and this is the other one. The aisle is
 * ISO class and gowned and white; the chase is where the same tools are
 * plumbed, powered and pumped, and a fab does not pretend it is clean. It
 * is the service half of the building - open deck instead of a filter
 * ceiling, banded headers instead of hidden ones, labels on everything,
 * and enough hardware that you can see what a tool actually costs to run.
 *
 * WHY IT NEEDED DRESSING. Until the doorways were fixed, most of this floor
 * could be seen through the bulkhead windows and entered at four places, so
 * what stood behind each window was all it needed. Now every bay's CHASE
 * ACCESS door opens, and a student who walks through one arrives in 9 m of
 * bare floor running the length of the building. The window dressing was a
 * diorama; this is the room it was pretending to be.
 *
 * WHAT IT TEACHES. A tool's face shows a recipe and a light tower. Its back
 * shows the four things the recipe actually consumes - process vacuum,
 * cooling water, clean dry air, and a route for what comes off - and every
 * one of them is a line you can follow from the tool to the header to the
 * wall. That is the point of letting anyone in here.
 *
 * WHAT THE SECOND PASS ADDED, and why each thing. The first version put the
 * deck and the spine in and stopped, and a walk through it found five
 * things wrong, in this order:
 *
 *   1. The back of every tool was a blank box. The mainframe comes through
 *      the wall as `buildTool` draws it and stops at a plain face; nothing
 *      on it said "this is the back of an etcher". It now carries what a
 *      rear service face carries - removable panels with their latches, a
 *      kick strip, a service light, a rear EMO, a hazard label, the cable
 *      entry down from the tool's own tray - and it is framed where it
 *      passes through the wall. The lines that hung in mid-air are landed:
 *      the foreline joins the pump it pumps, the cooling drops land on a
 *      manifold, the gas box is fed from the valve manifold box by way of
 *      a tube tray along the wall.
 *   2. The floor was one flat value, the same grey as the walls. It is a
 *      coated floor now - epoxy over the slab, satin, the finish of a grey
 *      area INSIDE the cleanroom envelope, which this is (a first cut put
 *      the sub-fab's dirty concrete here, which said basement under people
 *      in gowns) - with a room-sized decal drawn from the layout that
 *      shows the one wear a wiped floor shows: the walkway gone matte.
 *      Plant stands on housekeeping pads, as in the sub-fab.
 *   3. Nine metres of depth with nothing in the middle. The strip behind
 *      the support bank now holds what a bay's chase actually holds: a
 *      pair of gas cabinets, a chiller with its hoses on the floor, a parts
 *      rack, a step ladder at every third bay, and an extinguisher at
 *      every door. The bank itself is fed from the cable ladder.
 *   4. The bulkhead's chase face was the cleanroom's white. bays.ts skins
 *      it darker, the way the back of a panel wall is unfinished.
 *   5. The light was flat, because the strip fittings were emissive quads
 *      and nothing else. They now carry real lamps, joined to the fab's
 *      lamp pool under the same per-tier budget as everything else, and
 *      FabScene fades the hall's fills down while the eye is in here.
 *
 * INSTANCED THROUGHOUT, for the reason the utility gantry is: this is a few
 * thousand small objects and the hall has a draw-call budget built around
 * an integrated GPU. Everything that repeats is one mesh. Where the same
 * kind of thing comes in different sizes - a rear panel is as tall as its
 * tool - the instance carries a SCALE, and the geometry is a unit box or
 * a unit cylinder. One draw per kind, whatever the sizes.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js'
import { aisleDirection, bayNumber, BULKHEAD_Z, FAB, FACE_PROUD, SECOND_ROW_PIVOT_Z, secondTag, TOOLS, toolSize, type Tool } from './layout'
import { box, canvasTexture, cyl, EMO_RED, type FabMaterials } from './builders'
import { MAINTENANCE_TOOL } from './bays'
import { BALLROOM, onHallChange } from './hall'
import { SUBFAB } from './subfabLayout'

/** Where the chase starts and ends, measured from the hall's axis. */
const NEAR = BULKHEAD_Z
const H = FAB.ceilingHeight
// The hall's depth and length follow the layout (layout.ts `applyHallLayout`)
// and the switch is live, so these are refreshed before each rebuild.
let FAR = FAB.bayDepth + FAB.aisleHalfWidth
let L = FAB.length
onHallChange(() => {
  FAR = FAB.bayDepth + FAB.aisleHalfWidth
  L = FAB.length
})

/**
 * The service spine, in metres out from the bulkhead.
 *
 * Ordered the way a real chase stacks them: the exhaust duct is the biggest
 * thing in the room and gets the middle, the wet headers run inboard of it
 * where a valve can be reached off a step, and the cable tray runs outboard
 * and higher, because you never put cable under a water joint.
 */
const SPINE = {
  cda: { d: 1.05, y: 2.62, r: 0.055 },
  pcwSupply: { d: 1.32, y: 2.72, r: 0.085 },
  pcwReturn: { d: 1.55, y: 2.72, r: 0.085 },
  exhaust: { d: 2.15, y: 3.02, r: 0.3 },
  tray: { d: 2.95, y: 3.18, w: 0.44 },
} as const

/** The walkway: the strip kept clear between the tool backs and the banks. */
export const WALK_D = 3.55
/** Where the support bank stands, out from the wall. */
export const BANK_D = 4.6
/**
 * How far out from the bulkhead a tool's mainframe ends: `buildTool` runs
 * the body from the face line to `max(d, FACE_PROUD + 1)`, through the
 * wall. The chase crew stands off this face (chaseCrew.ts).
 */
export const mainframeBackD = (tool: Tool): number => Math.max(toolSize(tool).d, FACE_PROUD + 1.0) - FACE_PROUD
/** The housekeeping pad every piece of plant in here stands on. */
const PAD = { over: 0.13, high: 0.1, base: 0.008 }
/**
 * The building conduit along the bulkhead's chase face: above the window
 * heads (2.45 m plus frame) and above the gas tube tray (2.55 m), below the
 * CDA header's height so the two never read as one line.
 */
const WALL_RUN_Y = 2.72

/** Is this x over the sub-fab stairwell, which the chase floor opens into? */
const overStair = (x: number, side: number): boolean =>
  side > 0 && x > SUBFAB.stair.minX - 1.2 && x < SUBFAB.stair.maxX + 1.2

/**
 * The doorways and windows bays.ts cut through the bulkhead, as this side
 * of the wall needs them: an extinguisher goes beside every door, and the
 * door's own tool says which side of it is clear of the leaf's swing.
 */
export interface ChaseOpenings {
  doors: Array<{ x: number; side: number; toolId: string }>
  windows: Array<{ x: number; side: number; w: number }>
}

function lcg(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0x100000000
  }
}

/**
 * The deck overhead: a plain ribbed ceiling, not a filter face.
 *
 * This is the single thing that makes the chase read as a different room.
 * The hall's ceiling is 100% FFU and the same plane ran straight over the
 * bulkhead into the chase, so walking through a door took you from a
 * cleanroom into a slightly emptier cleanroom. A chase has a deck.
 */
function deckTexture(): THREE.CanvasTexture {
  return canvasTexture(
    128,
    128,
    (ctx) => {
      ctx.fillStyle = '#767d86'
      ctx.fillRect(0, 0, 128, 128)
      // The ribs of a steel deck, and the line where two sheets lap.
      ctx.strokeStyle = 'rgba(60, 66, 74, 0.28)'
      ctx.lineWidth = 3
      for (let y = 8; y < 128; y += 16) {
        ctx.beginPath()
        ctx.moveTo(0, y)
        ctx.lineTo(128, y)
        ctx.stroke()
      }
      ctx.strokeStyle = 'rgba(40, 46, 54, 0.5)'
      ctx.lineWidth = 4
      ctx.strokeRect(0, 0, 128, 128)
    },
    [L / 2.4, (FAR - NEAR) / 2.4],
  )
}

/**
 * An epoxy-coated floor, tiled every 3 m.
 *
 * NOT CONCRETE. This was the sub-fab's slab - aggregate speckle, cure
 * blotches - and it was wrong here, because the chase is INSIDE the
 * cleanroom envelope: a grey area, a class or two looser than the bay,
 * entered gowned through the bulkhead door. A grey-area floor is a
 * coated slab or a solid raised floor, wiped down, satin. What survives
 * of the slab under a coating is a faint mottle where the coats went on
 * at different thicknesses, and nothing else.
 */
function epoxyTexture(): THREE.CanvasTexture {
  const tex = canvasTexture(
    512,
    512,
    (ctx) => {
      const rnd = lcg(977)
      ctx.fillStyle = '#8a9097'
      ctx.fillRect(0, 0, 512, 512)
      for (let i = 0; i < 28; i++) {
        const r = 60 + rnd() * 160
        const x = rnd() * 512
        const y = rnd() * 512
        const grad = ctx.createRadialGradient(x, y, 0, x, y, r)
        grad.addColorStop(0, rnd() > 0.5 ? 'rgba(255,255,255,0.035)' : 'rgba(0,0,0,0.04)')
        grad.addColorStop(1, 'rgba(0,0,0,0)')
        ctx.fillStyle = grad
        ctx.fillRect(x - r, y - r, r * 2, r * 2)
      }
    },
    [L / 3, (FAR - NEAR) / 3],
  )
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

/**
 * The louvre on a support cabinet's door.
 *
 * Drawn rather than modelled, because a slat is 8 mm and there are six
 * hundred of them down this chase. Flat dark panels were what the first
 * version had, and a 0.6 m dark rectangle on a white cabinet reads as a
 * hole cut in it, not as the thing that lets an RF generator breathe.
 */
function louvreTexture(): THREE.CanvasTexture {
  return canvasTexture(32, 64, (ctx) => {
    ctx.fillStyle = '#2c3239'
    ctx.fillRect(0, 0, 32, 64)
    for (let y = 0; y < 64; y += 6) {
      ctx.fillStyle = '#79818a'
      ctx.fillRect(0, y, 32, 3)
      ctx.fillStyle = '#1b2026'
      ctx.fillRect(0, y + 3, 32, 1)
    }
  })
}

/** The grating over the chase's cable and drain trench. */
function grateTexture(): THREE.CanvasTexture {
  return canvasTexture(
    64,
    64,
    (ctx) => {
      ctx.fillStyle = '#2f353c'
      ctx.fillRect(0, 0, 64, 64)
      ctx.fillStyle = '#7d858f'
      for (let x = 2; x < 64; x += 8) ctx.fillRect(x, 0, 4, 64)
      ctx.fillStyle = '#5c646d'
      for (let y = 6; y < 64; y += 24) ctx.fillRect(0, y, 64, 3)
    },
    [L / 0.6, 1],
  )
}

/** A hazard label: chevrons and a legend, the sticker on every rear panel. */
function hazardMaterial(text: string): THREE.MeshBasicMaterial {
  const tex = canvasTexture(256, 112, (ctx) => {
    ctx.fillStyle = '#f0c231'
    ctx.fillRect(0, 0, 256, 112)
    ctx.fillStyle = '#1a1c1f'
    for (let x = -32; x < 256; x += 32) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x + 16, 0)
      ctx.lineTo(x + 32, 18)
      ctx.lineTo(x + 16, 18)
      ctx.closePath()
      ctx.fill()
    }
    ctx.fillStyle = '#1a1c1f'
    ctx.font = 'bold 40px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, 128, 66)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.MeshBasicMaterial({ map: tex })
}

/** The FIRE sign over an extinguisher: white on red, the pictogram every code agrees on. */
function fireSignMaterial(): THREE.MeshBasicMaterial {
  const tex = canvasTexture(128, 128, (ctx) => {
    ctx.fillStyle = '#c8232c'
    ctx.fillRect(0, 0, 128, 128)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(52, 34, 24, 60)
    ctx.fillRect(40, 30, 48, 10)
    ctx.beginPath()
    ctx.moveTo(76, 44)
    ctx.lineTo(100, 26)
    ctx.lineTo(104, 32)
    ctx.lineTo(82, 50)
    ctx.closePath()
    ctx.fill()
    ctx.font = 'bold 22px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('FIRE', 64, 110)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.MeshBasicMaterial({ map: tex })
}

/**
 * A pipe identification band: the colour and the legend a real header
 * carries every few metres, because a pipe with no label is a pipe nobody
 * may work on. These are the only saturated colours in the building above
 * floor level, and they are here rather than in the hall for the reason
 * the utility gantry gives: the code is a service convention, and this is
 * the service side of the wall.
 */
function bandMaterial(colour: string, text: string): THREE.MeshBasicMaterial {
  const tex = canvasTexture(256, 64, (ctx) => {
    ctx.fillStyle = colour
    ctx.fillRect(0, 0, 256, 64)
    ctx.fillStyle = '#12161b'
    ctx.font = 'bold 30px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, 128, 34)
  })
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.MeshBasicMaterial({ map: tex })
}

/**
 * Every tool's asset tag, on the top corner of its rear service face, on
 * one canvas behind one material.
 *
 * The single most useful object in a service corridor. A chase is 136 m of
 * near-identical hardware with no tool faces and no bay signs to read, and
 * a technician who has come through a door to work on ETCH-01 has to be
 * able to tell which back is ETCH-01's. Atlased, and the quads merged, for
 * the reason the andon boards are: twenty tags is twenty draws otherwise.
 *
 * ON THE MAINFRAME, NOT ON THE WALL. It hung on the wall over the tool,
 * and from the walkway the wall over a cluster tool is behind its own
 * transfer module and chambers; a plate tried higher sat exactly behind
 * the PCW headers. A vendor's rear nameplate is on the top corner of the
 * service face, which is where a technician looks - and a ballroom, with
 * no wall at all, needs it there too.
 */
const TAG_W = 256
const TAG_H = 64
/** Off the wall by more than the chase-face skin bays.ts lays on it (14 mm at 9 mm). */
const ON_WALL = 0.125

/** A stencilled plate: what it says, where it hangs, how big, which way it faces. */
interface Plate {
  text: string
  x: number
  y: number
  z: number
  w: number
  h: number
  side: number
}

/** One atlas, one material, one merged mesh for a set of plates. */
function buildPlates(plates: Plate[]): THREE.Mesh {
  const canvas = document.createElement('canvas')
  canvas.width = TAG_W
  canvas.height = TAG_H * Math.max(plates.length, 1)
  const ctx = canvas.getContext('2d')!
  const quads: THREE.BufferGeometry[] = []
  const m = new THREE.Matrix4()
  plates.forEach((plate, i) => {
    const y0 = i * TAG_H
    ctx.fillStyle = '#e6e9ec'
    ctx.fillRect(0, y0, TAG_W, TAG_H)
    ctx.fillStyle = '#20262d'
    ctx.fillRect(0, y0, 10, TAG_H)
    ctx.fillStyle = '#20262d'
    ctx.font = 'bold 34px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(plate.text, TAG_W / 2 + 5, y0 + TAG_H / 2 + 2)

    const geo = new THREE.PlaneGeometry(plate.w, plate.h)
    const uv = geo.attributes.uv
    const v1 = 1 - i / plates.length
    const v0 = 1 - (i + 1) / plates.length
    for (let k = 0; k < uv.count; k++) uv.setY(k, v0 + uv.getY(k) * (v1 - v0))
    // Facing into the chase, which is away from the hall.
    geo.rotateY(plate.side > 0 ? 0 : Math.PI)
    quads.push(geo.applyMatrix4(m.makeTranslation(plate.x, plate.y, plate.z)))
  })
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.Mesh(mergeGeometries(quads, false), new THREE.MeshBasicMaterial({ map: tex }))
}

/**
 * Each tool's asset tag on the top corner of its rear service face - and,
 * in Fab B, its copy's tag on the copy's back across the service aisle:
 * the copy is the tool turned half a turn about the aisle's centreline, so
 * its plate is this one mirrored in x about the tool and in z about the
 * pivot, facing the other way.
 */
function tagPlates(tools: Tool[]): Plate[] {
  return tools.flatMap((tool) => {
    const side = Math.sign(tool.position[2])
    // The mainframe's back, as `buildTool` runs it through the wall; the
    // plate sits a hand in from the corner, over the panel band, proud of
    // the panels (0.016 + 0.03 / 2) so it is on them rather than in them.
    const size = toolSize(tool)
    const depth = Math.max(size.d, FACE_PROUD + 1.0)
    const mirror = aisleDirection(tool) > 0 ? -1 : 1
    const dx = mirror * (size.w / 2 - 0.55)
    const z = side * (NEAR + depth - FACE_PROUD + 0.036)
    const own: Plate = { text: tool.assetTag, x: tool.position[0] - dx, y: size.h - 0.26, z, w: 0.72, h: 0.18, side }
    if (!BALLROOM) return [own]
    const copy: Plate = { ...own, text: secondTag(tool.assetTag), x: tool.position[0] + dx, z: side * 2 * SECOND_ROW_PIVOT_Z - z, side: -side }
    return [own, copy]
  })
}

/**
 * The bay number over the chase side of every door. The aisle side says
 * CHASE ACCESS and carries the andon; the chase side said nothing, and a
 * technician walking a corridor of near-identical doors has to know which
 * one puts them back in bay 04.
 */
function doorPlates(doors: ChaseOpenings['doors']): Plate[] {
  return doors.flatMap((door) => {
    const tool = TOOLS.find((t) => t.id === door.toolId)
    if (!tool) return []
    return [{ text: `BAY ${bayNumber(tool)}`, x: door.x, y: 2.38, z: door.side * (BULKHEAD_Z + ON_WALL), w: 0.6, h: 0.15, side: door.side }]
  })
}

/**
 * The floor's WEAR, one decal per side, drawn in the chase's own
 * coordinates from the layout.
 *
 * WHAT A CLEANROOM FLOOR WEARS. This used to carry the sub-fab's marks -
 * oil under the pumps, dust along the walls, scuffs inside the doors, the
 * slab's joints - and every one of them was a basement mark on a floor
 * that is inside the cleanroom envelope and wiped down on a schedule. The
 * one thing a coated grey-area floor does show is the walkway: a lane
 * where the coating has gone matte under shoe covers, faintly lighter
 * than the satin either side, fading toward the two ends where nobody
 * goes. That is all this draws now. The marks the layout still supplies
 * (doors, pumps, banks, chillers, MCC sections) stay in the interface so
 * the callers do not change, and so the sub-fab's rule - a mark has a
 * cause - is still the rule if a cause returns.
 *
 * It lies above the coating at 7 mm, under the pads at 8 mm, and it is
 * transparent so the mottle still comes through.
 */
interface WearMarks {
  doors: number[]
  pumps: number[]
  banks: number[]
  chillers: number[]
  mccs: number[]
}
function wearTexture(marks: WearMarks, seed: number): THREE.CanvasTexture {
  const D = FAR - NEAR
  const cw = 2048
  const ch = 192
  const tex = canvasTexture(cw, ch, (ctx) => {
    const rnd = lcg(seed)
    const px = (x: number) => ((x + L / 2) / L) * cw
    const pd = (d: number) => (d / D) * ch
    const mx = cw / L
    const mz = ch / D
    /** A soft dark blot, in metres, at (x, d) - d out from the bulkhead. */
    const blot = (x: number, d: number, rx: number, rz: number, alpha: number, tone = '52,48,44') => {
      const cx = px(x)
      const cz = pd(d)
      const r = rx * mx
      const grad = ctx.createRadialGradient(cx, cz, 0, cx, cz, r)
      grad.addColorStop(0, `rgba(${tone},${alpha})`)
      grad.addColorStop(0.55, `rgba(${tone},${alpha * 0.55})`)
      grad.addColorStop(1, `rgba(${tone},0)`)
      ctx.save()
      ctx.translate(cx, cz)
      ctx.scale(1, (rz * mz) / r)
      ctx.translate(-cx, -cz)
      ctx.fillStyle = grad
      ctx.fillRect(cx - r, cz - r, r * 2, r * 2)
      ctx.restore()
    }

    ctx.clearRect(0, 0, cw, ch)
    void marks
    // The walkway: the coating gone matte under shoe covers, a shade
    // lighter than the satin either side, fading toward the ends.
    for (let x = -L / 2 + 1; x < L / 2 - 1; x += 0.45) {
      const endFade = Math.min(1, (x + L / 2 - 1) / 5, (L / 2 - 1 - x) / 5)
      blot(x, WALK_D + (rnd() - 0.5) * 0.5, 1.1, 0.5, 0.07 * endFade * (0.7 + rnd() * 0.6), '236,238,240')
    }
  })
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping
  tex.repeat.set(1, 1)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

export interface Chase {
  group: THREE.Group
  /** The strip fittings' lamps, for the fab's shared pool. */
  lamps: THREE.PointLight[]
  /** Show the chase only when the student is in it. */
  update(eye: THREE.Vector3): void
  /** Footprints of everything in here a person can walk into. */
  colliders(): Array<{ minX: number; maxX: number; minZ: number; maxZ: number }>
  /**
   * The PCW header's high-temperature beacon behind the etcher: green while
   * the chilled water is at setpoint, red when the house loop runs warm.
   * Driven from the live house state (FabScene.updateHouse).
   */
  setPcwWarm(warm: boolean): void
}

/**
 * Where the chase switches on, measured from the hall's axis.
 *
 * All of this is behind a solid wall from the aisle, and drawing it there
 * cost 2.3 ms a frame - 50 draw calls and the fill behind them - for an
 * image that does not contain one pixel of it. So it is gated, the way the
 * sub-fab's room is.
 *
 * The threshold is INSIDE the bulkhead's own colliders on purpose. Those
 * stop the student at |z| = 2.68 everywhere except a doorway, so the only
 * place this line can be crossed is while walking through a door, framed by
 * the reveal - which is where a pop is least visible. Put it any further in
 * and leaning at a viewing window would flick the whole chase on.
 *
 * What a window shows is unaffected: the per-window dressing and the tool's
 * own chase-side hardware are built in bays.ts and builders.ts, not here,
 * and they are exactly what a window showed before this file existed.
 */
const SHOW_AT = BULKHEAD_Z - 0.5

export function buildChase(mats: FabMaterials, openings: ChaseOpenings): Chase {
  const g = new THREE.Group()
  // The PCW beacon's material: one lamp, its colour driven live - see setPcwWarm.
  const pcwBeaconMat = new THREE.MeshStandardMaterial({ color: 0x184a2a, emissive: 0x1f8f4a, emissiveIntensity: 0.9, roughness: 0.4 })
  const colliders: Array<{ minX: number; maxX: number; minZ: number; maxZ: number }> = []
  const lamps: THREE.PointLight[] = []

  const place = (geometry: THREE.BufferGeometry, material: THREE.Material, m: THREE.Matrix4[]): void => {
    if (m.length === 0) return
    const mesh = new THREE.InstancedMesh(geometry, material, m.length)
    m.forEach((mat, i) => mesh.setMatrixAt(i, mat))
    mesh.instanceMatrix.needsUpdate = true
    // Spans the whole building; culling it per instance costs more than
    // drawing it, and the bulkhead already depth-rejects the lot.
    mesh.frustumCulled = false
    g.add(mesh)
  }
  const at = (x: number, y: number, z: number) => new THREE.Matrix4().makeTranslation(x, y, z)
  /**
   * A unit shape, placed and SIZED. Rotation is about x then y: `rx` of
   * PI/2 turns a unit cylinder to run along z, `rz` of PI/2 along x.
   */
  const scratchP = new THREE.Vector3()
  const scratchQ = new THREE.Quaternion()
  const scratchE = new THREE.Euler()
  const scratchS = new THREE.Vector3()
  const sized = (x: number, y: number, z: number, sx: number, sy: number, sz: number, rx = 0, ry = 0, rz = 0) =>
    new THREE.Matrix4().compose(
      scratchP.set(x, y, z),
      scratchQ.setFromEuler(scratchE.set(rx, ry, rz)),
      scratchS.set(sx, sy, sz),
    )
  const unitBox = box(1, 1, 1)
  const unitCyl = cyl(1, 1, 1, 10)
  const unitQuad = new THREE.PlaneGeometry(1, 1)

  const deckMat = new THREE.MeshStandardMaterial({ map: deckTexture(), roughness: 0.9, metalness: 0.1 })
  const grateMat = new THREE.MeshStandardMaterial({ map: grateTexture(), roughness: 0.8, metalness: 0.35 })
  const pipeMat = new THREE.MeshStandardMaterial({ color: 0xaeb4bb, roughness: 0.5, metalness: 0.5 })
  const ductMat = new THREE.MeshStandardMaterial({ color: 0xb9bfc6, roughness: 0.62, metalness: 0.45 })
  const trayMat = new THREE.MeshStandardMaterial({ color: 0x5f666e, roughness: 0.7, metalness: 0.45 })
  const steelMat = new THREE.MeshStandardMaterial({ color: 0x7c838b, roughness: 0.55, metalness: 0.55 })
  const panelMat = new THREE.MeshStandardMaterial({ color: 0xd9dde1, roughness: 0.55 })
  const stripMat = new THREE.MeshBasicMaterial({ color: 0xfdfbf4 })
  // The rear of a mainframe is not the aisle's white: it is the unpainted
  // panel grey a vendor ships the service side in, with a satin to it.
  const rearMat = new THREE.MeshStandardMaterial({ color: 0xc3c8cd, roughness: 0.58, metalness: 0.12 })
  const darkMat = new THREE.MeshStandardMaterial({ color: 0x33383e, roughness: 0.8, metalness: 0.2 })
  const hoseMat = new THREE.MeshStandardMaterial({ color: 0x2d4f78, roughness: 0.92, metalness: 0.0 })
  const concreteDark = new THREE.MeshStandardMaterial({ color: 0x6b7076, roughness: 0.95 })
  const cardboard = new THREE.MeshStandardMaterial({ color: 0x9b8b72, roughness: 0.95 })
  const hazardMat = hazardMaterial('400 V')
  const fireMat = fireSignMaterial()
  const BANDS = {
    exhaust: bandMaterial('#e0a53c', 'EXHAUST'),
    pcwS: bandMaterial('#4b8fd0', 'PCW SUP'),
    pcwR: bandMaterial('#3f6f9e', 'PCW RET'),
    cda: bandMaterial('#d8dde2', 'CDA'),
  }

  const beams: THREE.Matrix4[] = []
  const strips: THREE.Matrix4[] = []
  const hangers: THREE.Matrix4[] = []
  const trapeze: THREE.Matrix4[] = []
  const rungs: THREE.Matrix4[] = []
  const bandOf: Record<string, THREE.Matrix4[]> = { exhaust: [], pcwS: [], pcwR: [], cda: [] }
  const vmb: THREE.Matrix4[] = []
  const vmbGlass: THREE.Matrix4[] = []
  const disconnect: THREE.Matrix4[] = []
  const disconnectLever: THREE.Matrix4[] = []
  const forelines: THREE.Matrix4[] = []
  const drops: THREE.Matrix4[] = []
  const boots: THREE.Matrix4[] = []
  const banks: THREE.Matrix4[] = []
  const louvres: THREE.Matrix4[] = []
  const screens: THREE.Matrix4[] = []
  const trunks: THREE.Matrix4[] = []
  const junctions: THREE.Matrix4[] = []
  const backDrops: THREE.Matrix4[] = []
  const mcc: THREE.Matrix4[] = []
  const mccDoor: THREE.Matrix4[] = []
  // The second pass, all sized instances of a unit box or cylinder.
  const rearPanels: THREE.Matrix4[] = []
  const latches: THREE.Matrix4[] = []
  const kicks: THREE.Matrix4[] = []
  const serviceLights: THREE.Matrix4[] = []
  const brackets: THREE.Matrix4[] = []
  const rearEmos: THREE.Matrix4[] = []
  const hazards: THREE.Matrix4[] = []
  const frames: THREE.Matrix4[] = []
  const conduits: THREE.Matrix4[] = []
  const boxes: THREE.Matrix4[] = []
  const pipes: THREE.Matrix4[] = []
  const flanges: THREE.Matrix4[] = []
  const tubes: THREE.Matrix4[] = []
  const tubeTrays: THREE.Matrix4[] = []
  const pads: THREE.Matrix4[] = []
  const anchors: THREE.Matrix4[] = []
  const cabinets: THREE.Matrix4[] = []
  const stacks: THREE.Matrix4[] = []
  const placards: THREE.Matrix4[] = []
  const chillers: THREE.Matrix4[] = []
  const chillerVents: THREE.Matrix4[] = []
  const hoses: THREE.Matrix4[] = []
  const uprights: THREE.Matrix4[] = []
  const shelves: THREE.Matrix4[] = []
  const cartons: THREE.Matrix4[] = []
  const rails: THREE.Matrix4[] = []
  const ladderRungs: THREE.Matrix4[] = []
  const extinguishers: THREE.Matrix4[] = []
  const fireSigns: THREE.Matrix4[] = []
  const drains: THREE.Matrix4[] = []
  const padlocks: THREE.Matrix4[] = []
  const lockTags: THREE.Matrix4[] = []
  const walkLineMat = new THREE.MeshStandardMaterial({ color: 0xe8b83a, roughness: 0.8 })
  // Satin: a coated floor, not a slab.
  const floorMat = new THREE.MeshStandardMaterial({ map: epoxyTexture(), roughness: 0.52, metalness: 0.03 })
  const louvreMat = new THREE.MeshStandardMaterial({ map: louvreTexture(), roughness: 0.75, metalness: 0.3 })

  /** Remap a floor plane's UVs to chase coordinates: x over the hall, d over the depth. */
  const chaseUv = (geo: THREE.PlaneGeometry, side: number): THREE.PlaneGeometry => {
    // The plane is built in xy and turned to lie flat; before the turn its
    // y is what becomes world -z (side < 0) or +z. Read positions as laid.
    const pos = geo.attributes.position
    const uv = geo.attributes.uv
    for (let i = 0; i < pos.count; i++) {
      const wx = pos.getX(i)
      const wz = -pos.getY(i)
      const d = Math.abs(wz) - NEAR
      uv.setXY(i, (wx + L / 2) / L, side > 0 ? d / (FAR - NEAR) : 1 - d / (FAR - NEAR))
    }
    return geo
  }

  const doorsOn = (side: number) => openings.doors.filter((o) => o.side === side)

  for (const side of [-1, 1] as const) {
    const z = (d: number): number => side * (NEAR + d)
    const marks: WearMarks = { doors: doorsOn(side).map((o) => o.x), pumps: [], banks: [], chillers: [], mccs: [] }

    // --- the deck, and the beams under it -----------------------------------
    // Both decks face DOWN. Mirroring the rotation by side, the way a floor
    // would be mirrored, turned one of them over: it faced the slab above
    // and was backface-culled, so one chase kept the filter ceiling it was
    // supposed to lose and the other did not.
    //
    // Not in a ballroom, where this side of the row is the same room as
    // the aisle and keeps the aisle's filter ceiling: the deck, its beams
    // and its battens are what make a chase a different room, and a
    // ballroom has no different room. `buildRoom` runs two more luminaire
    // rows over this side there, where the battens hang here.
    if (!BALLROOM) {
      const deck = new THREE.Mesh(new THREE.PlaneGeometry(L - 0.4, FAR - NEAR), deckMat)
      deck.rotation.x = Math.PI / 2
      deck.position.set(0, H - 0.03, (side * (NEAR + FAR)) / 2)
      g.add(deck)

      for (let x = -L / 2 + 4; x < L / 2 - 4; x += 6) {
        beams.push(at(x, H - 0.22, side * (NEAR + FAR) / 2))
      }

      // Two rows of strip fittings, which is all the light a chase gets. The
      // emissive batten is the visible source; the lamp is every second one,
      // over the walkway, which is where the light is wanted.
      for (const d of [1.7, 5.4]) {
        for (let x = -L / 2 + 3; x < L / 2 - 3; x += 3.4) strips.push(at(x, H - 0.4, z(d)))
      }
    }
    // THE CULL RADIUS IS 4 M, AND THAT NUMBER IS LOAD-BEARING. Each lamp
    // joins the fab's per-tier budget, which is the worst-case count of
    // lamps within their radius of any walkable spot - and the aisle is a
    // walkable spot 4.35 m from the nearest of these, through the wall. At
    // the sub-fab's 13 m radius four of them counted from the aisle, and
    // every shader in the building paid for four lights that are switched
    // off there. At 4 m none does, and 6.8 m apart they still overlap along
    // the walkway (3.4 m over, 1.4 m up: 3.7 m to the nearest). A ballroom
    // has no wall and keeps them lit, so there they count like any lamp.
    for (let x = -L / 2 + 3; x < L / 2 - 3; x += 6.8) {
      const lamp = new THREE.PointLight(0xe9eef2, 7, 13, 2)
      lamp.position.set(x, H - 0.5, z(WALK_D))
      lamp.userData.litho = false
      lamp.userData.dim = 0.85
      lamp.userData.cullRadius = BALLROOM ? 11 : 4.0
      // Lit by FabScene in proportion to how far the eye is into the chase:
      // a point light does not stop at a wall, and at full strength from
      // the aisle these would have lit the tool faces from behind.
      //
      // In a ballroom there is no wall for them to stop at and nothing
      // wrong with a mainframe lit from its service side, so they are
      // simply the downlights over that side, on all the time.
      lamp.userData.chase = !BALLROOM
      lamps.push(lamp)
    }

    // --- the service spine --------------------------------------------------
    //
    // NOT IN A BALLROOM. The spine hangs under a deck; under a filter
    // ceiling nothing hangs but the hoist track, and every header a 300 mm
    // fab runs is in the sub-fab, coming up to each tool through the floor.
    // So the ballroom's service side has no duct, no wet headers, no cable
    // ladder overhead - and every line below that reached up to them
    // reaches down through the raised floor instead.
    const runs = BALLROOM
      ? []
      : ([
          { s: SPINE.cda, mat: pipeMat, band: 'cda' },
          { s: SPINE.pcwSupply, mat: pipeMat, band: 'pcwS' },
          { s: SPINE.pcwReturn, mat: pipeMat, band: 'pcwR' },
        ] as const)
    for (const r of runs) {
      const geo = cyl(r.s.r, r.s.r, L - 1.0, 12)
      geo.rotateZ(Math.PI / 2)
      const mesh = new THREE.Mesh(geo, r.mat)
      mesh.position.set(0, r.s.y, z(r.s.d))
      g.add(mesh)
      for (let x = -L / 2 + 7; x < L / 2 - 7; x += 11) bandOf[r.band].push(at(x, r.s.y, z(r.s.d)))
    }
    if (!BALLROOM) {
      const duct = new THREE.Mesh(cyl(SPINE.exhaust.r, SPINE.exhaust.r, L - 1.0, 16), ductMat)
      duct.geometry.rotateZ(Math.PI / 2)
      duct.position.set(0, SPINE.exhaust.y, z(SPINE.exhaust.d))
      g.add(duct)
      for (let x = -L / 2 + 7; x < L / 2 - 7; x += 11) bandOf.exhaust.push(at(x, SPINE.exhaust.y, z(SPINE.exhaust.d)))

      // The cable ladder: two stringers and a rung every 300 mm.
      for (const dz of [-SPINE.tray.w / 2, SPINE.tray.w / 2]) {
        const stringer = new THREE.Mesh(box(L - 1.0, 0.09, 0.03), trayMat)
        stringer.position.set(0, SPINE.tray.y, z(SPINE.tray.d) + dz)
        g.add(stringer)
      }
      for (let x = -L / 2 + 0.6; x < L / 2 - 0.6; x += 0.3) rungs.push(at(x, SPINE.tray.y - 0.03, z(SPINE.tray.d)))

      // Hangers: a trapeze under the wet headers every 3 m, on drop rods.
      for (let x = -L / 2 + 3; x < L / 2 - 3; x += 3) {
        trapeze.push(at(x, 2.6, z(1.3)))
        for (const d of [0.85, 1.78]) hangers.push(at(x, (2.6 + H) / 2, z(d)))
      }
    }

    // --- what stands at the back of each tool -------------------------------
    let bayIndex = 0
    for (const tool of TOOLS) {
      if (tool.position[2] === 0 || Math.sign(tool.position[2]) !== side) continue
      const x = tool.position[0]
      const size = toolSize(tool)
      const w = size.w
      const h = size.h
      const mirror = aisleDirection(tool) > 0 ? -1 : 1
      // The mainframe's back: `buildTool` runs the body from the face line
      // to this depth, through the wall, so this is the face a technician
      // in here actually stands at.
      const depth = Math.max(size.d, FACE_PROUD + 1.0)
      const backD = depth - FACE_PROUD
      const backZ = z(backD)
      /** A hand proud of the back, into the room. */
      const proud = (e: number): number => backZ + side * e
      bayIndex++

      // The PCW high-temperature beacon, on the supply header where it
      // drops to the etcher. Every tool on the loop has a flow switch and
      // a temperature switch at its drop; this one is drawn because the
      // etcher is where a warm loop shows first (two of its three chambers
      // cool through it) and where a technician chasing the symptom ends
      // up standing. Green at setpoint, red when the house water is warm -
      // the one thing in this room that says the fault is not in the tool.
      if (tool.id === 'etch') {
        const bx = x + mirror * 0.9
        const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.14, 8), mats.steel)
        stem.position.set(bx, SPINE.pcwSupply.y + SPINE.pcwSupply.r + 0.07, z(SPINE.pcwSupply.d))
        g.add(stem)
        const lamp = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.06, 0.12, 12), pcwBeaconMat)
        lamp.position.set(bx, SPINE.pcwSupply.y + SPINE.pcwSupply.r + 0.2, z(SPINE.pcwSupply.d))
        g.add(lamp)
      }

      // --- the rear service face ------------------------------------------
      //
      // Removable panels across the full width, each with its quarter-turn
      // latch at the right edge; a kick strip at the foot; a service light
      // on a bracket over the top; the rear EMO where a hand reaches it
      // from the walkway; and the hazard label the panel over the RF match
      // carries. The label says 400 V because that is what is behind it.
      const n = Math.max(2, Math.round(w / 0.85))
      const pw = (w - 0.1) / n
      for (let i = 0; i < n; i++) {
        const cx = x - (w - 0.1) / 2 + pw * (i + 0.5)
        // The etcher has one panel OFF: the technician the chase crew
        // stands here (chaseCrew.ts) is working inside it. The opening is
        // a dark recess with the match network and its cable in it, and
        // the panel leans against the mainframe beside it, latch out.
        if (tool.id === 'etch' && i === 1) {
          kicks.push(sized(cx, h / 2 + 0.03, proud(0.006), pw - 0.05, h - 0.34, 0.01))
          boxes.push(sized(cx - 0.12, 1.2, proud(0.09), 0.3, 0.24, 0.16))
          conduits.push(sized(cx + 0.18, 1.0, proud(0.07), 0.02, 0.9, 0.02))
          rearPanels.push(sized(cx + pw + 0.35, (h - 0.34) / 2 + 0.02, proud(0.24), pw - 0.05, h - 0.34, 0.03, -side * 0.18))
          latches.push(sized(cx + pw + 0.35 + pw / 2 - 0.09, 1.15, proud(0.24 + 0.02), 0.04, 0.16, 0.03, -side * 0.18))
          continue
        }
        rearPanels.push(sized(cx, h / 2 + 0.03, proud(0.016), pw - 0.05, h - 0.34, 0.03))
        latches.push(sized(cx + pw / 2 - 0.09, 1.15, proud(0.04), 0.04, 0.16, 0.03))
      }
      kicks.push(sized(x, 0.075, proud(0.01), w, 0.15, 0.02))
      serviceLights.push(sized(x, h + 0.14, proud(0.2), 0.6, 0.05, 0.09))
      brackets.push(sized(x, h + 0.09, proud(0.1), 0.06, 0.05, 0.22))
      rearEmos.push(sized(x - mirror * (w / 2 - 0.3), 1.45, proud(0.05), 0.045, 0.06, 0.045, Math.PI / 2))
      hazards.push(sized(x + mirror * (w / 2 - 0.5), 1.72, proud(0.035), 0.32, 0.14, 1, 0, side > 0 ? 0 : Math.PI))

      // --- Fab B's copy of this tool, across the service aisle -----------
      //
      // Its back faces this one's back (builders.ts `buildSecondRow` turns
      // the copy half a turn about the aisle's centreline), so it carries
      // the same service face, turned: its local x is mirrored and "proud"
      // is toward the hall's centre. Its gas panel and disconnect stand on
      // their plinths beside it, like this tool's.
      if (BALLROOM) {
        const cm = -mirror
        const cz = side * (2 * SECOND_ROW_PIVOT_Z - NEAR - backD)
        const cp = (e: number): number => cz - side * e
        const czv = (d: number): number => side * (2 * SECOND_ROW_PIVOT_Z - NEAR - d)
        for (let i = 0; i < n; i++) {
          const cx = x - (w - 0.1) / 2 + pw * (i + 0.5)
          rearPanels.push(sized(cx, h / 2 + 0.03, cp(0.016), pw - 0.05, h - 0.34, 0.03))
          latches.push(sized(cx - pw / 2 + 0.09, 1.15, cp(0.04), 0.04, 0.16, 0.03))
        }
        kicks.push(sized(x, 0.075, cp(0.01), w, 0.15, 0.02))
        serviceLights.push(sized(x, h + 0.14, cp(0.2), 0.6, 0.05, 0.09))
        brackets.push(sized(x, h + 0.09, cp(0.1), 0.06, 0.05, 0.22))
        rearEmos.push(sized(x - cm * (w / 2 - 0.3), 1.45, cp(0.05), 0.045, 0.06, 0.045, Math.PI / 2))
        hazards.push(sized(x + cm * (w / 2 - 0.5), 1.72, cp(0.035), 0.32, 0.14, 1, 0, side > 0 ? Math.PI : 0))
        const cvx = x + cm * (w / 2 + 0.05)
        vmb.push(at(cvx, 1.42, czv(0.16)))
        vmbGlass.push(at(cvx, 1.62, czv(0.28)))
        boxes.push(sized(cvx, 0.45, czv(0.16), 0.86, 0.9, 0.22))
        colliders.push({ minX: cvx - 0.5, maxX: cvx + 0.5, minZ: Math.min(czv(0), czv(0.32)), maxZ: Math.max(czv(0), czv(0.32)) })
        const cdx = x - cm * (w / 2 + 0.5)
        disconnect.push(at(cdx, 1.55, czv(0.18)))
        disconnectLever.push(at(cdx + 0.2, 1.55, czv(0.34)))
        boxes.push(sized(cdx, 0.6, czv(0.18), 0.4, 1.2, 0.26))
        colliders.push({ minX: cdx - 0.3, maxX: cdx + 0.3, minZ: Math.min(czv(0), czv(0.34)), maxZ: Math.max(czv(0), czv(0.34)) })
      }

      // The frame round the cut-out where the mainframe passes through the
      // wall: steel angle, on the chase face, which is how a bulkhead is
      // actually sealed to a tool.
      if (!BALLROOM) {
        frames.push(sized(x, h + 0.06, z(0.13), w + 0.14, 0.06, 0.06))
        for (const sx of [-1, 1]) frames.push(sized(x + sx * (w / 2 + 0.04), (h + 0.06) / 2, z(0.13), 0.06, h + 0.09, 0.06))
      }

      // The cable entry: flex conduit from the tool's own tray (2.9 m over
      // the mainframe, on the bulkhead line) back along the roof and down
      // the rear face into a junction box. The tray was a bar in mid-air.
      const ex = x - mirror * (w / 2 - 0.22)
      const runLen = backD - 0.6 - 0.1
      // The tray is on the wall line at 2.9 m in a bay-and-chase hall and
      // on the mainframe's roof in a ballroom (`buildTool`); the conduit
      // leaves it at whichever height it is.
      const trayY = BALLROOM ? h + 0.03 : 2.9
      conduits.push(sized(ex, trayY, z(0.6 + runLen / 2), 0.03, runLen, 0.03, Math.PI / 2))
      conduits.push(sized(ex, (trayY + 1.78) / 2, proud(0.08), 0.03, trayY - 1.78, 0.03))
      boxes.push(sized(ex, 1.66, proud(0.06), 0.22, 0.26, 0.1))

      // The valve manifold box: the panel every process gas is isolated at,
      // on the chase face of the bulkhead beside the tool it feeds.
      //
      // In a ballroom there is no wall to hang it on: it stands on its own
      // plinth beside the tool, a free-standing gas panel, which is a real
      // arrangement and the only one that leaves it at hand height.
      const vx = x + mirror * (w / 2 + 0.05)
      vmb.push(at(vx, 1.42, z(0.16)))
      vmbGlass.push(at(vx, 1.62, z(0.28)))
      if (BALLROOM) boxes.push(sized(vx, 0.45, z(0.16), 0.86, 0.9, 0.22))
      // Its power: a junction box on the wall conduit over the window heads
      // and a drop to the panel's top, in front of the tube tray. A gas
      // panel is an interlocked, powered thing, and nothing fed it.
      if (!BALLROOM) {
        junctions.push(at(vx + mirror * 0.3, WALL_RUN_Y, z(0.2)))
        conduits.push(sized(vx + mirror * 0.3, (WALL_RUN_Y - 0.17 + 1.95) / 2, z(0.33), 0.022, WALL_RUN_Y - 0.17 - 1.95, 0.022))
      }
      colliders.push({ minX: vx - 0.5, maxX: vx + 0.5, minZ: Math.min(z(0), z(0.32)), maxZ: Math.max(z(0), z(0.32)) })

      // The disconnect: the switch the tool is locked out at, which is why
      // there is a lockout tag on THRM-04's panel on the other side. On a
      // pedestal in a ballroom, for the same reason as the manifold box.
      //
      // ON THE MAINFRAME'S SIDE, not on the wall behind the gas box. It hung
      // on the wall at the gas box's own x, and `buildTool` stands that box
      // 0.8 m out from the wall, so from the walkway the switch was behind
      // it - the one control in here a lockout depends on, invisible. The
      // side of the mainframe between the gas box and the back is clear,
      // in sight of the tool, and puts the handle at 1.55 m, which is
      // where a disconnect handle is allowed to be.
      const dx = x - mirror * (w / 2 + 0.5)
      let lever: [number, number, number]
      if (BALLROOM) {
        disconnect.push(sized(dx, 1.55, z(0.18), 0.44, 0.72, 0.28))
        lever = [dx + 0.2, 1.55, z(0.34)]
        boxes.push(sized(dx, 0.6, z(0.18), 0.4, 1.2, 0.26))
        colliders.push({ minX: dx - 0.3, maxX: dx + 0.3, minZ: Math.min(z(0), z(0.34)), maxZ: Math.max(z(0), z(0.34)) })
      } else {
        const sx = x - mirror * (w / 2 + 0.15)
        const sz = z(backD - 0.27)
        disconnect.push(sized(sx, 1.55, sz, 0.44, 0.72, 0.28, 0, Math.PI / 2))
        lever = [x - mirror * (w / 2 + 0.32), 1.55, sz - side * 0.2]
        colliders.push({
          minX: Math.min(x - mirror * (w / 2), x - mirror * (w / 2 + 0.36)), maxX: Math.max(x - mirror * (w / 2), x - mirror * (w / 2 + 0.36)),
          minZ: sz - 0.3, maxZ: sz + 0.3,
        })
      }
      disconnectLever.push(at(...lever))
      // The tool that is down has its lock on THIS switch: a padlock
      // through the lever and the tag hanging off it. The tag on the open
      // panel in the aisle is the other half of the same lockout, and a
      // student who has read one and walks round should find the other.
      if (tool.id === MAINTENANCE_TOOL) {
        padlocks.push(sized(lever[0], 1.45, lever[2], 0.04, 0.055, 0.022))
        lockTags.push(sized(lever[0], 1.33, lever[2], 0.075, 0.12, 0.004))
      }

      // The gas: out of the VMB's top into a tube tray along the wall, and
      // down out of the tray into the gas box that stands beside the
      // mainframe on the other side. Three sticks, because a process tool
      // runs on more than one gas and one line would read as a conduit.
      //
      // Wall-mounted, so bay-and-chase only: in a ballroom the gas comes up
      // through the floor inside the panel's plinth and the tubing is never
      // in view.
      const gx = x - mirror * (w / 2 + 0.5)
      if (!BALLROOM) {
        tubeTrays.push(sized(x, 2.55, z(0.2), w + 1.3, 0.04, 0.12))
        for (const k of [-0.14, 0, 0.14]) {
          tubes.push(sized(vx + k, (1.95 + 2.53) / 2, z(0.24), 0.012, 2.53 - 1.95, 0.012))
          tubes.push(sized(gx + k, (1.9 + 2.53) / 2, z(0.24), 0.012, 2.53 - 1.9, 0.012))
          tubes.push(sized(gx + k, 1.9, z((0.24 + 0.8) / 2), 0.012, 0.8 - 0.24, 0.012, Math.PI / 2))
        }
      }

      // The foreline: from the pump box `buildTool` stands beside the
      // mainframe, up to the exhaust duct. This is the line that makes the
      // duct mean something - without it the duct is a pipe on a ceiling.
      // It now leaves the pump's roof through an elbow and a flange rather
      // than starting in the air behind it.
      //
      // In a ballroom the duct is in the sub-fab, so the line turns DOWN at
      // the same elbow and goes through the floor on a flange.
      //
      // Not in a ballroom at all: the pump is in the sub-fab there (there
      // is no pump box beside the mainframe - see `buildTool`), and the
      // line from the chamber to it runs down inside the tool.
      const px = x + mirror * (w / 2 + 0.55)
      if (!BALLROOM && !overStair(px, side)) {
        forelines.push(at(px, 2.35, z(1.95)))
        boots.push(at(px, SPINE.exhaust.y - 0.28, z(SPINE.exhaust.d)))
        pipes.push(sized(px, 1.62, z((1.3 + 1.95) / 2), 0.07, 1.95 - 1.3, 0.07, Math.PI / 2))
        flanges.push(sized(px, 1.62, z(1.3), 0.1, 0.05, 0.1, Math.PI / 2))
        flanges.push(sized(px, 1.78, z(1.95), 0.1, 0.05, 0.1))
        marks.pumps.push(px)
      }
      // Cooling water down to the tool, off the trapeze, landing on a
      // manifold on the mainframe's side wall rather than in the air. In a
      // ballroom it comes UP to the same manifold, out of the floor.
      for (const dz of [1.32, 1.55]) drops.push(at(px - mirror * 0.28, BALLROOM ? 0.46 : 1.9, z(dz)))
      boxes.push(sized(x + mirror * (w / 2 + 0.07), 1.06, z(1.435), 0.12, 0.3, 0.5))

      // --- the support bank --------------------------------------------
      //
      // The half of a process tool that is not the process tool: the RF
      // generator, the chiller, the controller rack. Real ones stand in
      // the chase behind the mainframe they serve, trunked to it, and this
      // floor has been carrying them in the aisle instead - a liberty
      // taken back when the aisle was the only room anyone could walk in.
      //
      // It is also what makes a 9 m chase a room rather than a corridor.
      // One bank per bay leaves the gaps between bays clear end to end, so
      // the chase is still walkable along its whole length.
      //
      // BAY-AND-CHASE ONLY - the bank and everything in the strip behind
      // it. This is the real difference between the two designs, more than
      // the wall: a chase is WHERE the support equipment lives, and a
      // ballroom has no such room, so its pumps, chillers and racks are in
      // the sub-fab, its gas cabinets in a gas room, its switchgear in an
      // electrical room. The service side of a ballroom row is the tool
      // backs, their gas panels, and a clear aisle - nothing else stands
      // on the clean floor.
      const bx = x - mirror * 0.3
      if (!BALLROOM && !overStair(bx, side)) {
        const padTop = PAD.base + PAD.high
        banks.push(at(bx, 0.98 + padTop, z(BANK_D)))
        // Proud of the cabinet face at z(4.21), not on it: coplanar they
        // z-fought, and every bank down the chase moired.
        for (const ddx of [-0.7, 0.05]) louvres.push(at(bx + ddx, 1.35 + padTop, z(4.17)))
        screens.push(at(bx + 0.85, 1.52 + padTop, z(4.15)))
        // The trunk from the bank to the back of the tool it serves.
        trunks.push(at(bx, 2.4, z(3.45)))
        pad(pads, anchors, bx, z(BANK_D), 2.4, 0.78)
        // Its feed, down off the cable ladder over the walkway and along
        // to the cabinet top: the ladder ran the length of the room and
        // nothing left it.
        // (In a ballroom the ladder is in the sub-fab and the feed comes up
        // through the pad, out of sight.)
        if (!BALLROOM) {
          conduits.push(sized(bx + 0.9, (SPINE.tray.y - 0.06 + 2.3) / 2, z(SPINE.tray.d), 0.03, SPINE.tray.y - 0.06 - 2.3, 0.03))
          conduits.push(sized(bx + 0.9, 2.3, z((SPINE.tray.d + BANK_D) / 2), 0.03, BANK_D - SPINE.tray.d, 0.03, Math.PI / 2))
          conduits.push(sized(bx + 0.9, (2.3 + 1.93 + padTop) / 2, z(BANK_D), 0.03, 2.3 - 1.93 - padTop, 0.03))
        }
        colliders.push({
          minX: bx - 1.3, maxX: bx + 1.3,
          minZ: Math.min(z(4.2), z(5.0)), maxZ: Math.max(z(4.2), z(5.0)),
        })
        marks.banks.push(bx)

        // --- the rest of the bay's plant, in the strip behind the bank ---
        //
        // Two gas cabinets, vented up to the deck, with the placard a
        // cabinet holding a toxic or a corrosive carries on its door; a
        // chiller for the tool's own loop, with its hoses on the floor to
        // the bank; a parts rack; and at every third bay the step ladder
        // somebody left out. None of it is decorative - a chase without
        // gas cabinets is a chase with nothing to run the tool on.
        // Each piece is checked against the stairwell on its own x: the
        // bank clearing the hole does not mean a cabinet 2 m along does.
        const cabD = 6.9
        if (!overStair(bx + 2.28, side)) {
          for (const k of [0, 1]) cabinets.push(sized(bx + 1.95 + k * 0.66, 1.0 + padTop, z(cabD), 0.6, 2.0, 0.6))
          pad(pads, anchors, bx + 2.28, z(cabD), 1.3, 0.6)
          for (const k of [0, 1]) {
            const cx = bx + 1.95 + k * 0.66
            stacks.push(sized(cx, (2.0 + padTop + H - 0.3) / 2, z(cabD), 0.06, H - 0.3 - 2.0 - padTop, 0.06))
            placards.push(sized(cx, 1.5, z(cabD - 0.31), 0.3, 0.2, 1, 0, side > 0 ? Math.PI : 0))
          }
          colliders.push({ minX: bx + 1.6, maxX: bx + 2.95, minZ: Math.min(z(cabD - 0.35), z(cabD + 0.35)), maxZ: Math.max(z(cabD - 0.35), z(cabD + 0.35)) })
        }

        const chD = 5.85
        chillers.push(sized(bx + 0.3, 0.65 + padTop, z(chD), 1.0, 1.3, 0.8))
        chillerVents.push(sized(bx + 0.3, 1.3 + padTop + 0.011, z(chD), 0.8, 0.02, 0.6))
        pad(pads, anchors, bx + 0.3, z(chD), 1.0, 0.8)
        // Braided hose from the chiller's front down to the floor and
        // across to the bank's back, which is what it cools.
        for (const hx of [bx + 0.05, bx + 0.45]) {
          hoses.push(sized(hx, 0.3 + padTop, z(chD - 0.42), 0.022, 0.6, 0.022))
          hoses.push(sized(hx, 0.04, z((chD - 0.42 + 5.0) / 2), 0.022, chD - 0.42 - 5.0, 0.022, Math.PI / 2))
        }
        colliders.push({ minX: bx - 0.25, maxX: bx + 0.85, minZ: Math.min(z(chD - 0.45), z(chD + 0.45)), maxZ: Math.max(z(chD - 0.45), z(chD + 0.45)) })
        marks.chillers.push(bx + 0.3)

        const rx = bx - 1.9
        if (!overStair(rx, side)) {
          for (const sx of [-0.58, 0.58]) for (const sz of [-0.23, 0.23]) uprights.push(sized(rx + sx, 0.9, z(cabD) + sz, 0.04, 1.8, 0.04))
          for (const sy of [0.08, 0.68, 1.28]) shelves.push(sized(rx, sy, z(cabD), 1.2, 0.03, 0.5))
          cartons.push(sized(rx - 0.28, 0.27, z(cabD), 0.5, 0.35, 0.4))
          cartons.push(sized(rx + 0.3, 0.24, z(cabD), 0.4, 0.3, 0.36))
          cartons.push(sized(rx - 0.2, 0.86, z(cabD), 0.44, 0.32, 0.4))
          cartons.push(sized(rx + 0.32, 1.44, z(cabD), 0.36, 0.28, 0.34))
          colliders.push({ minX: rx - 0.65, maxX: rx + 0.65, minZ: Math.min(z(cabD - 0.3), z(cabD + 0.3)), maxZ: Math.max(z(cabD - 0.3), z(cabD + 0.3)) })
        }

        if (bayIndex % 3 === 1 && !overStair(bx - 3.3, side)) {
          // Leaning on the outer wall, feet a stride out: tilt about x,
          // toward the wall, which is +d on this side.
          const lx = bx - 3.3
          const tilt = -side * 0.24
          const ly = 1.2
          const ld = FAR - NEAR - 0.16 - 0.55
          for (const sx of [-0.2, 0.2]) rails.push(sized(lx + sx, ly, z(ld), 0.02, 2.5, 0.02, tilt))
          for (let i = 0; i < 8; i++) {
            const t = (i + 0.5) / 8 - 0.5
            ladderRungs.push(sized(lx, ly + t * 2.5 * Math.cos(tilt), z(ld) + side * -Math.sin(tilt) * t * 2.5, 0.4, 0.03, 0.03, tilt))
          }
          colliders.push({ minX: lx - 0.3, maxX: lx + 0.3, minZ: Math.min(z(ld - 0.4), z(ld + 0.4)), maxZ: Math.max(z(ld - 0.4), z(ld + 0.4)) })
        }
      }

      // A floor drain behind every wet tool, where its day tanks stand.
      // (Not through a raised floor: a ballroom's drains are in the sub-fab.)
      if (!BALLROOM && (tool.kind === 'wetbench' || tool.kind === 'cmp' || tool.kind === 'plating')) {
        drains.push(sized(x + mirror * 1.2, 0.0065, z(backD + 0.95), 0.3, 1, 0.3, -Math.PI / 2))
      }
    }

    // --- an extinguisher at every door ----------------------------------
    //
    // On the side of the door away from the hinge: the leaf swings into
    // the chase about its far edge and would sweep anything mounted on
    // that side of the frame. The bracket, the bottle, and the sign.
    for (const door of doorsOn(side)) {
      const tool = TOOLS.find((t) => t.id === door.toolId)
      const toward = tool ? Math.sign(tool.position[0] - door.x) || 1 : 1
      const fx = door.x + toward * 0.75
      extinguishers.push(sized(fx, 0.85, z(0.2), 0.075, 0.5, 0.075))
      brackets.push(sized(fx, 1.04, z(0.17), 0.1, 0.06, 0.12))
      fireSigns.push(sized(fx, 1.52, z(ON_WALL), 0.2, 0.2, 1, 0, side > 0 ? 0 : Math.PI))
    }

    // --- the back wall --------------------------------------------------
    // Conduit and junction boxes down the outer wall, and the odd safety
    // fixture: the far side of a chase is not a blank, it is where the
    // building's own services come down.
    // The same building conduit along the bulkhead's chase face, over the
    // window heads: the wall between the tube trays and the panels was
    // blank, and a chase wall is where a building's conduit runs.
    if (!BALLROOM) {
      const wallRun = new THREE.Mesh(cyl(0.03, 0.03, L - 2, 8), pipeMat)
      wallRun.geometry.rotateZ(Math.PI / 2)
      wallRun.position.set(0, WALL_RUN_Y, z(0.2))
      g.add(wallRun)
      for (let x = -L / 2 + 3; x < L / 2 - 3; x += 2.4) brackets.push(sized(x, WALL_RUN_Y - 0.04, z(0.14), 0.05, 0.03, 0.12))
    }

    // In a ballroom the outer wall is a cleanroom panel wall like any
    // other, and the building's services stay behind it.
    const backZ = side * (FAR - 0.16)
    if (!BALLROOM) {
      const conduitRun = new THREE.Mesh(cyl(0.05, 0.05, L - 2, 8), pipeMat)
      conduitRun.geometry.rotateZ(Math.PI / 2)
      conduitRun.position.set(0, 2.35, backZ)
      g.add(conduitRun)
      for (let x = -L / 2 + 6; x < L / 2 - 6; x += 8) {
        junctions.push(at(x, 2.05, backZ))
        backDrops.push(at(x, 1.4, backZ))
      }
    }

    // The power line-up: switchgear and motor control against the outer
    // wall, in runs of six sections. A 9 m chase has a strip out here that
    // nothing else wants, and this is what a real one puts in it - the
    // distribution the whole row of tools is fed from, as far from the
    // process side of the room as the building can put it. A ballroom
    // keeps it in an electrical room, off the clean floor.
    const mccZ = side * (FAR - 0.62)
    for (let x = -L / 2 + 8; x < L / 2 - 8; x += 15) {
      if (BALLROOM || overStair(x, side)) continue
      for (let k = 0; k < 6; k++) {
        const cx = x + k * 1.02
        mcc.push(at(cx, 1.05 + PAD.base + PAD.high, mccZ))
        mccDoor.push(at(cx, 1.25 + PAD.base + PAD.high, mccZ - side * 0.31))
        marks.mccs.push(cx)
      }
      pad(pads, anchors, x + 2.5 * 1.02, mccZ, 6 * 1.02, 0.6)
      colliders.push({
        minX: x - 0.55, maxX: x + 5 * 1.02 + 0.55,
        minZ: Math.min(mccZ - side * 0.32, side * FAR), maxZ: Math.max(mccZ - side * 0.32, side * FAR),
      })
    }

    // --- floor ------------------------------------------------------------
    //
    // Sealed concrete, laid over the hall's perforated white one.
    //
    // The raised floor is there so that filtered air can be pulled down
    // through it, and that is a cleanroom's business, not a chase's - so
    // the value that a chase reads at is set by a poured floor, not by a
    // white deck. With the hall's floor running under it the room was as
    // bright as the cleanroom it is supposed to be the back of, which
    // undid most of what the deck overhead had just bought.
    //
    // Split around the sub-fab stairwell, which is a hole in this floor and
    // has to stay one. The wear decal is split the same way, because a
    // decal over a hole is a stain floating over a stair.
    const floorRuns: Array<[number, number]> = side > 0
      ? [[-L / 2 + 0.4, SUBFAB.stair.minX - 0.5], [SUBFAB.stair.maxX + 0.5, L / 2 - 0.4]]
      : [[-L / 2 + 0.4, L / 2 - 0.4]]
    const wearMat = new THREE.MeshStandardMaterial({
      map: wearTexture(marks, side > 0 ? 311 : 733),
      transparent: true,
      depthWrite: false,
      roughness: 0.97,
      polygonOffset: true,
      polygonOffsetFactor: -2,
    })
    //
    // A ballroom keeps the raised floor right across: the service side is
    // the same cleanroom, pulling the same air down through the same
    // perforated tiles, and a poured slab there would be the one thing on
    // the floor saying "different room". No slab, no wear on it.
    for (const [x0, x1] of BALLROOM ? [] : floorRuns) {
      if (x1 - x0 < 1) continue
      const geo = new THREE.PlaneGeometry(x1 - x0, FAR - NEAR - 0.2)
      geo.translate((x0 + x1) / 2, -(side * (NEAR + FAR)) / 2, 0)
      chaseUv(geo, side)
      geo.translate(-(x0 + x1) / 2, (side * (NEAR + FAR)) / 2, 0)
      const slab = new THREE.Mesh(geo, floorMat)
      slab.rotation.x = -Math.PI / 2
      slab.position.set((x0 + x1) / 2, 0.003, (side * (NEAR + FAR)) / 2)
      slab.receiveShadow = true
      g.add(slab)
      const wear = new THREE.Mesh(geo, wearMat)
      wear.rotation.x = -Math.PI / 2
      wear.position.set((x0 + x1) / 2, 0.007, (side * (NEAR + FAR)) / 2)
      wear.renderOrder = 1
      g.add(wear)
    }

    // The walkway line: the strip kept clear between the tool backs and the
    // support banks, which is the route anyone servicing this side walks.
    for (const [x0, x1] of floorRuns) {
      if (x1 - x0 < 1) continue
      const line = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, 0.08), walkLineMat)
      line.rotation.x = -Math.PI / 2
      line.position.set((x0 + x1) / 2, 0.005, z(WALK_D))
      g.add(line)
    }

    // The trench: cable and drain, grated over, running the length of the
    // chase a stride off the wall. Split around the stairwell, which is a
    // hole in this same floor.
    const runsX: Array<[number, number]> = side > 0
      ? [[-L / 2 + 1, SUBFAB.stair.minX - 1.4], [SUBFAB.stair.maxX + 1.4, L / 2 - 1]]
      : [[-L / 2 + 1, L / 2 - 1]]
    // Not through a raised floor: a ballroom's cable and drain run in the
    // sub-fab, and the tiles over them are tiles.
    for (const [x0, x1] of BALLROOM ? [] : runsX) {
      if (x1 - x0 < 1) continue
      const grate = new THREE.Mesh(new THREE.PlaneGeometry(x1 - x0, 0.55), grateMat)
      grate.rotation.x = -Math.PI / 2
      grate.position.set((x0 + x1) / 2, 0.006, z(0.85))
      g.add(grate)
    }
  }

  place(box(0.16, 0.34, FAR - NEAR - 0.2), steelMat, beams)
  place(box(2.6, 0.07, 0.16), stripMat, strips)
  place(cyl(0.018, 0.018, H - 2.6, 6), steelMat, hangers)
  place(box(0.06, 0.05, 1.35), steelMat, trapeze)
  place(box(0.035, 0.02, SPINE.tray.w - 0.06), trayMat, rungs)
  for (const [key, mat] of Object.entries(BANDS)) {
    const s = key === 'exhaust' ? SPINE.exhaust.r : key === 'cda' ? SPINE.cda.r : SPINE.pcwSupply.r
    place(cyl(s + 0.006, s + 0.006, 0.26, 16), mat, bandOf[key])
  }
  place(box(0.9, 1.05, 0.24), panelMat, vmb)
  place(box(0.42, 0.3, 0.02), mats.screen, vmbGlass)
  place(unitBox, mats.toolPanel, disconnect)
  place(box(0.05, 0.16, 0.05), steelMat, disconnectLever)
  place(cyl(0.07, 0.07, 1.5, 10), pipeMat, forelines)
  place(cyl(0.075, 0.075, 0.5, 10), ductMat, boots)
  place(cyl(0.03, 0.03, 1.5, 8), pipeMat, drops)
  place(box(2.4, 1.9, 0.78), mats.toolBody, banks)
  place(box(0.62, 0.7, 0.02), louvreMat, louvres)
  place(box(0.34, 0.24, 0.02), mats.screen, screens)
  place(box(0.24, 0.16, 1.4), trayMat, trunks)
  place(box(0.28, 0.34, 0.16), mats.toolPanel, junctions)
  place(cyl(0.022, 0.022, 1.2, 6), pipeMat, backDrops)
  place(box(1.0, 2.1, 0.6), mats.toolPanel, mcc)
  // A recessed panel, not a hole: a door the same family of grey as the
  // section it is in, which is what a line-up looks like. In the tray grey
  // it read as a row of dark openings.
  place(box(0.84, 1.5, 0.02), new THREE.MeshStandardMaterial({ color: 0xb4bac1, roughness: 0.6 }), mccDoor)

  // The second pass: unit shapes, sized per instance.
  place(unitBox, rearMat, rearPanels)
  place(unitBox, darkMat, latches)
  place(unitBox, darkMat, kicks)
  place(unitBox, stripMat, serviceLights)
  place(unitBox, steelMat, brackets)
  place(unitCyl, EMO_RED, rearEmos)
  place(unitQuad, hazardMat, hazards)
  place(unitBox, steelMat, frames)
  place(unitCyl, trayMat, conduits)
  place(unitBox, mats.toolPanel, boxes)
  place(unitCyl, pipeMat, pipes)
  place(unitCyl, steelMat, flanges)
  place(unitCyl, pipeMat, tubes)
  place(unitBox, trayMat, tubeTrays)
  place(unitBox, concreteDark, pads)
  place(unitCyl, steelMat, anchors)
  place(unitBox, mats.toolPanel, cabinets)
  place(unitCyl, ductMat, stacks)
  place(unitQuad, hazardMaterial('TOXIC'), placards)
  place(unitBox, mats.toolPanel, chillers)
  place(unitBox, louvreMat, chillerVents)
  place(unitCyl, hoseMat, hoses)
  place(unitBox, steelMat, uprights)
  place(unitBox, panelMat, shelves)
  place(unitBox, cardboard, cartons)
  place(unitCyl, steelMat, rails)
  place(unitBox, steelMat, ladderRungs)
  place(unitCyl, EMO_RED, extinguishers)
  place(unitQuad, fireMat, fireSigns)
  place(unitQuad, grateMat, drains)
  place(unitBox, darkMat, padlocks)
  place(unitBox, new THREE.MeshStandardMaterial({ color: 0xc8232c, roughness: 0.7 }), lockTags)

  // The tag plates are on the mainframe backs, so they hold in a ballroom
  // as well as behind a wall.
  g.add(buildPlates([...tagPlates(TOOLS.filter((t) => t.position[2] !== 0)), ...doorPlates(openings.doors)]))

  // Built VISIBLE, and hidden by the first frame's `update`. The prewarm
  // render in FabCanvas is what compiles this file's programs, and it runs
  // once, before the loop starts - so a chase that begins hidden compiles
  // nothing, and pays for all ten of its shaders the first time a student
  // steps through a door. Which is the mid-walk freeze this project has
  // already spent two sessions removing.
  //
  // In a ballroom there is no wall to hide behind: the service side is in
  // view from the aisle, so it is drawn whenever this floor is.
  const update = (eye: THREE.Vector3): void => {
    g.visible = eye.y > -0.5 && (BALLROOM || Math.abs(eye.z) > SHOW_AT)
  }

  const setPcwWarm = (warm: boolean): void => {
    pcwBeaconMat.color.setHex(warm ? 0x5a1414 : 0x184a2a)
    pcwBeaconMat.emissive.setHex(warm ? 0xe83030 : 0x1f8f4a)
    pcwBeaconMat.emissiveIntensity = warm ? 1.6 : 0.9
  }

  return { group: g, lamps, update, colliders: () => colliders, setPcwWarm }

  /**
   * A housekeeping pad under a footprint, with its anchor bolts. The same
   * rule as the sub-fab: plant does not sit on the slab, it sits on a
   * 100 mm pad a hand's breadth bigger than the machine, and the bolts go
   * through the frame, not the overhang.
   */
  function pad(padList: THREE.Matrix4[], boltList: THREE.Matrix4[], x: number, zc: number, w: number, d: number): void {
    padList.push(sized(x, PAD.base + PAD.high / 2, zc, w + PAD.over * 2, PAD.high, d + PAD.over * 2))
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        boltList.push(sized(x + sx * (w / 2 - 0.05), PAD.base + PAD.high + 0.02, zc + sz * (d / 2 - 0.05), 0.016, 0.05, 0.016))
      }
    }
  }
}
